import { ErrorCode, REQUEST_ID_HEADER, TENANT_HEADER, type ApiResponse } from '@kainos/shared';
import { config } from '../config.js';
import { getAccessToken, getActiveTenantId, reportSessionExpired } from '../auth/session.js';
import { ApiError } from './api-error.js';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(path.replace(/^\//, ''), `${config.apiBaseUrl.replace(/\/$/, '')}/`);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value));
  }
  return url.toString();
}

/**
 * Thin fetch wrapper for the KainOS API.
 *
 * It attaches the Cognito token and the active tenant to every call, and
 * unwraps the `{ data }` / `{ error }` envelope so callers work with plain
 * values and catch a typed `ApiError`.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const token = await getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const tenantId = getActiveTenantId();
  if (tenantId) headers[TENANT_HEADER] = tenantId;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (cause) {
    throw new ApiError(0, {
      code: ErrorCode.INTERNAL,
      message: 'No pudimos conectar con el servidor.',
      ...(cause instanceof Error ? {} : {}),
    });
  }

  if (response.status === 204) return undefined as T;

  const requestId = response.headers.get(REQUEST_ID_HEADER) ?? undefined;
  let payload: ApiResponse<T>;
  try {
    payload = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError(response.status, {
      code: ErrorCode.INTERNAL,
      message: 'La respuesta del servidor no es válida.',
      requestId,
    });
  }

  if (!response.ok || 'error' in payload) {
    const error =
      'error' in payload
        ? payload.error
        : { code: ErrorCode.INTERNAL, message: 'Error desconocido.' };

    // A 401 means the token is gone for good — refreshing already happened
    // upstream, in `getAccessToken`. Let the app fall back to sign-in rather
    // than filling every screen with errors nothing can clear.
    if (response.status === 401) reportSessionExpired();

    throw new ApiError(response.status, { ...error, requestId: error.requestId ?? requestId });
  }

  return payload.data;
}

export const http = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
